import React, { useEffect, useRef, useState } from 'react';

const HERO_PHOTO_URL =
  'https://customer-assets-39nsmqrw.emergentagent.net/job_archetype-finder-4/artifacts/6ueth5r6_SALES%20PAGE%20IMAGES.jpg';

const BIO_PHOTO_URL =
  'https://customer-assets-39nsmqrw.emergentagent.net/job_archetype-finder-4/artifacts/jdx9y2ox_DSC_0917-Edit.webp';

const uncoverItems = [
  'Your birth prep archetype',
  'The specific kind of support she needs to feel steady',
  'Your free personalized toolkit, matched to your archetype',
];

const howItWorksSteps = [
  {
    num: '01',
    title: 'Answer ten questions',
    body: 'Each one narrows down your primary archetype.',
  },
  {
    num: '02',
    title: 'Meet your birth prep archetype',
    body: 'The Visionary. The Warrior. The Scholar. The Feeler. The Oracle. The Nurturer.',
  },
  {
    num: '03',
    title: 'Get your free personalized toolkit',
    body: 'A short, practical guide matched to what your archetype needs to feel steady.',
  },
];

/**
 * useParallax — returns a translateY value in px based on scroll position
 * so the image drifts slower than the text (subtle editorial parallax).
 */
function useParallax(strength = 0.14) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const viewportH = window.innerHeight || 1;
      // 0 when element enters bottom of viewport, 1 when it leaves the top
      const progress = 1 - (rect.top + rect.height / 2) / (viewportH + rect.height);
      setOffset(progress * viewportH * strength);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [strength]);
  return [ref, offset];
}

export const IntroScreen = ({ onStart }) => {
  const [heroPhotoRef, heroPhotoOffset] = useParallax(0.08);
  const [bioPhotoRef, bioPhotoOffset] = useParallax(0.10);

  return (
    <div data-testid="intro-screen" className="screen-enter w-full">
      {/* ============================================================
          HERO — Side-by-side split. Salt panel left, full-color photo
          right (no tint). Photo bleeds down into next section.
          Mobile: text first, photo below.
          ============================================================ */}
      <section
        className="w-full relative"
        style={{ backgroundColor: '#E8E0D5' }}
        data-testid="intro-hero-section"
      >
        <div className="grid md:grid-cols-[1fr_1.05fr] items-stretch">
          {/* Left: text column, Salt bg, left-aligned */}
          <div
            className="px-6 sm:px-12 lg:px-16 pt-16 sm:pt-24 pb-14 sm:pb-24 flex flex-col justify-center order-1"
            style={{ backgroundColor: '#E8E0D5' }}
          >
            <div className="mb-10" data-testid="intro-eyebrow">
              <span className="eyebrow-line" style={{ color: '#3D3830', fontSize: '13px', letterSpacing: '0.32em' }}>
                Free 3-Min Quiz
              </span>
            </div>

            <h1
              data-testid="intro-title"
              className="font-heading italic text-[40px] leading-[1.05] sm:text-[56px] sm:leading-[1.02] max-w-[560px]"
              style={{ fontWeight: 500, color: '#3D3830' }}
            >
              What do you need to feel ready for birth?
            </h1>

            <div className="mt-10 max-w-[540px]" data-testid="intro-subhead">
              <p
                className="font-body text-[22px] sm:text-[23px] leading-[1.5]"
                style={{ color: '#3D3830', fontStyle: 'normal' }}
              >
                Cookie-cutter birth prep doesn't work. (You're not a cookie.)
              </p>
              <p
                data-testid="intro-payoff"
                className="font-body italic mt-5 text-[20px] sm:text-[22px] leading-[1.6]"
                style={{ color: '#3D3830' }}
              >
                Every woman comes into this journey with a primary archetype. This quiz helps you identify yours so you can give her exactly what she needs to feel steady.
              </p>
            </div>

            <div className="mt-12">
              <button
                data-testid="intro-start-button"
                onClick={onStart}
                className="btn-primary"
                style={{ fontSize: '14px', padding: '18px 34px', letterSpacing: '0.24em' }}
              >
                Meet your archetype
              </button>
            </div>
          </div>

          {/* Right: full-color photo. Bleeds down into next Deep Cedar section. */}
          <div
            className="relative order-2"
            style={{ marginBottom: '-64px', zIndex: 2 }}
          >
            <div
              ref={heroPhotoRef}
              className="w-full h-[420px] sm:h-[560px] md:h-full overflow-hidden"
            >
              <img
                src={HERO_PHOTO_URL}
                alt="Pregnant belly held in soft evening light."
                data-testid="intro-hero-photo"
                className="w-full h-full object-cover"
                style={{
                  objectPosition: 'center 55%',
                  transform: `translateY(${heroPhotoOffset * -0.3}px) scale(1.06)`,
                  transition: 'transform 60ms linear',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          UNCOVER — Deep Cedar full-bleed. Photo above overlaps in.
          ============================================================ */}
      <section
        className="w-full"
        style={{ backgroundColor: '#3D3830', color: '#E8E0D5' }}
        data-testid="intro-uncover-section"
      >
        <div className="max-w-[1120px] mx-auto px-6 sm:px-12 lg:px-16 pt-32 sm:pt-40 pb-24 sm:pb-32">
          <div className="max-w-[720px]">
            <div
              className="section-label mb-6"
              style={{ color: '#B8A78F', fontSize: '13px' }}
            >
              What You'll Uncover
            </div>
            <ul className="flex flex-col">
              {uncoverItems.map((item, i) => (
                <li
                  key={i}
                  className="py-5 sm:py-6"
                  style={{
                    borderTop: i === 0 ? '1px solid rgba(184, 167, 143, 0.45)' : 'none',
                    borderBottom: '1px solid rgba(184, 167, 143, 0.45)',
                  }}
                  data-testid={`intro-uncover-item-${i}`}
                >
                  <span
                    className="font-body text-[19px] sm:text-[22px] leading-[1.45]"
                    style={{ color: '#E8E0D5', fontStyle: 'normal' }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============================================================
          BIO — Salt full-bleed. Bio photo bleeds up into Cedar above.
          ============================================================ */}
      <section
        className="w-full"
        style={{ backgroundColor: '#E8E0D5' }}
        data-testid="intro-bio-section"
      >
        <div className="max-w-[1240px] mx-auto grid md:grid-cols-[1fr_1.1fr] gap-0 items-stretch">
          {/* Photo, overlapping up into the Cedar section above */}
          <div
            className="relative"
            style={{ marginTop: '-64px', zIndex: 3 }}
          >
            <div
              ref={bioPhotoRef}
              className="w-full h-[440px] sm:h-[560px] md:h-[620px] overflow-hidden"
            >
              <img
                src={BIO_PHOTO_URL}
                alt="Kara Mears, Midwife (CNM)"
                data-testid="intro-bio-photo"
                className="w-full h-full object-cover"
                style={{
                  objectPosition: 'center 20%',
                  transform: `translateY(${bioPhotoOffset * -0.35}px) scale(1.05)`,
                  transition: 'transform 60ms linear',
                }}
              />
            </div>
          </div>

          {/* Bio copy */}
          <div className="px-6 sm:px-12 lg:px-16 py-16 sm:py-24 flex flex-col justify-center">
            <div
              className="section-label mb-6"
              style={{ color: '#B8A78F' }}
            >
              Made for you by
            </div>
            <h2
              className="font-heading italic text-[38px] sm:text-[52px] leading-[1.05]"
              style={{ color: '#3D3830', fontWeight: 500 }}
            >
              Kara Mears
            </h2>
            <p
              className="font-body text-[16px] mt-3"
              style={{ color: '#B8A78F', letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 12 }}
            >
              Certified Nurse Midwife &amp; Mum
            </p>

            <p
              className="font-heading text-[24px] sm:text-[28px] leading-[1.35] mt-8"
              style={{ color: '#3D3830', fontStyle: 'normal', fontWeight: 500 }}
            >
              <em className="italic">You</em> are the birth plan.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          HOW IT WORKS — Cedar Tan full-bleed
          ============================================================ */}
      <section
        className="w-full"
        style={{ backgroundColor: '#B8A78F' }}
        data-testid="intro-how-it-works"
      >
        <div className="max-w-[1000px] mx-auto px-6 sm:px-12 lg:px-16 py-20 sm:py-28">
          <div
            className="section-label mb-10"
            style={{ color: '#3D3830', fontSize: '13px' }}
          >
            How it works
          </div>

          <ul className="flex flex-col">
            {howItWorksSteps.map((step, i) => (
              <li
                key={step.num}
                className="grid grid-cols-[auto_1fr] gap-x-8 sm:gap-x-12 py-8 sm:py-10 items-baseline"
                style={{
                  borderTop: i === 0 ? '1px solid rgba(61, 56, 48, 0.28)' : 'none',
                  borderBottom: '1px solid rgba(61, 56, 48, 0.28)',
                }}
                data-testid={`intro-step-${step.num}`}
              >
                <span
                  className="font-heading italic text-[54px] sm:text-[72px] leading-none"
                  style={{ color: '#3D3830', fontWeight: 500 }}
                >
                  {step.num}
                </span>
                <div>
                  <h3
                    className="font-heading italic text-[22px] sm:text-[28px] leading-[1.2] mb-2"
                    style={{ color: '#3D3830', fontWeight: 500 }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="font-body text-[16px] sm:text-[17px] leading-[1.6]"
                    style={{ color: '#3D3830', fontStyle: 'normal' }}
                  >
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-14 sm:mt-16 text-center">
            <button
              data-testid="intro-how-cta-button"
              onClick={onStart}
              className="btn-primary"
              style={{
                fontSize: '14px',
                padding: '18px 34px',
                letterSpacing: '0.24em',
                backgroundColor: '#CEC5D4',
                color: '#3D3830',
              }}
            >
              Discover your archetype now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
